<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center">
          <h4>{{ $t('accounting_accounts_statement') }}</h4>
          <b-button
            variant="secondary"
            @click="$router.go(-1)"
          >
            {{ $t('accounting_accounts_back') }}
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-card class="p-0 filter-card rounded-lg mb-3">
          <b-row>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('accounting_accounts_from_date')">
                <b-form-input
                  v-model="filters.date_from"
                  type="date"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('accounting_accounts_to_date')">
                <b-form-input
                  v-model="filters.date_to"
                  type="date"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="d-flex align-items-end"
            >
              <b-button
                variant="primary"
                @click="fetchStatement"
              >
                {{ $t('accounting_accounts_view_button') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>

        <div
          v-if="loadStatement"
          class="text-center"
        >
          <b-spinner
            variant="primary"
            label="Spinning"
          />
        </div>
        <div v-else-if="statement">
          <!-- Account Info -->
          <b-card
            v-if="statement.account"
            class="mb-3"
          >
            <h5 class="mb-3">
              {{ statement.account.name }} ({{ statement.account.code }})
            </h5>
            <b-row>
              <b-col
                cols="12"
                md="4"
              >
                <p class="mb-0 text-muted">
                  {{ $t('accounting_accounts_type') }}
                </p>
                <h6>
                  <b-badge :variant="getTypeVariant(statement.account.type && statement.account.type.value)">
                    {{ $i18n.locale === 'ar' ? (statement.account.type && statement.account.type.label_ar) || '' : (statement.account.type && statement.account.type.label) || '' }}
                  </b-badge>
                </h6>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <p class="mb-0 text-muted">
                  {{ $t('accounting_accounts_currency') }}
                </p>
                <h6>{{ statement.account.currency }}</h6>
              </b-col>
              <b-col
                v-if="statement.period"
                cols="12"
                md="4"
              >
                <p class="mb-0 text-muted">
                  {{ $t('accounting_accounts_period') }}
                </p>
                <h6>{{ statement.period.start }} - {{ statement.period.end }}</h6>
              </b-col>
            </b-row>
          </b-card>

          <!-- Summary -->
          <b-card class="mb-3">
            <h5 class="mb-3">
              {{ $t('accounting_accounts_summary') }}
            </h5>
            <b-row>
              <b-col
                cols="12"
                md="4"
              >
                <div class="text-center">
                  <p class="mb-0 text-muted">
                    {{ $t('accounting_accounts_total_debits') }}
                  </p>
                  <h4 class="text-danger">
                    {{ formatCurrency(statement.total_debits || 0, (statement.account && statement.account.currency) || 'EGP') }}
                  </h4>
                </div>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <div class="text-center">
                  <p class="mb-0 text-muted">
                    {{ $t('accounting_accounts_total_credits') }}
                  </p>
                  <h4 class="text-success">
                    {{ formatCurrency(statement.total_credits || 0, (statement.account && statement.account.currency) || 'EGP') }}
                  </h4>
                </div>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <div class="text-center">
                  <p class="mb-0 text-muted">
                    {{ $t('accounting_accounts_current_balance') }}
                  </p>
                  <h4>{{ formatCurrency(statement.current_balance || 0, (statement.account && statement.account.currency) || 'EGP') }}</h4>
                </div>
              </b-col>
            </b-row>
          </b-card>

          <!-- Entries -->
          <b-card>
            <h5 class="mb-3">
              {{ $t('accounting_accounts_entries') }}
            </h5>
            <b-table
              v-if="statement.entries && statement.entries.length > 0"
              striped
              :items="statement.entries"
              :fields="fields"
              responsive
            >
              <template #cell(entry_type)="data">
                <b-badge :variant="data.item.entry_type === 'debit' ? 'danger' : 'success'">
                  {{ data.item.entry_type === 'debit' ? $t('accounting_accounts_debit') : $t('accounting_accounts_credit') }}
                </b-badge>
              </template>
              <template #cell(amount)="data">
                {{ formatCurrency(data.item.amount, (statement.account && statement.account.currency) || 'EGP') }}
              </template>
              <template #cell(created_at)="data">
                {{ formatDate(data.item.created_at) }}
              </template>
            </b-table>
            <div
              v-else
              class="text-center text-muted py-4"
            >
              {{ $t('accounting_accounts_no_entries') }}
            </div>
          </b-card>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BRow,
  BCol,
  BBadge,
  BSpinner,
  BTable,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BTable,
    BFormGroup,
    BFormInput,
  },
  computed: {
    ...mapGetters({
      statement: 'accounting/accounts/statement',
      loadStatement: 'accounting/accounts/loadStatement',
    }),
    accountId() {
      return this.$route.params.id
    },
    fields() {
      return [
        { key: 'created_at', label: this.$t('accounting_transactions_date'), sortable: true },
        { key: 'entry_type', label: this.$t('accounting_accounts_entry_type') },
        { key: 'amount', label: this.$t('accounting_transactions_amount') },
        { key: 'description', label: this.$t('accounting_accounts_description') },
      ]
    },
  },
  data() {
    return {
      filters: {
        date_from: null,
        date_to: null,
      },
    }
  },
  async mounted() {
    await this.fetchStatement()
  },
  methods: {
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return ''
      return new Intl.DateTimeFormat(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(date))
    },
    getTypeVariant(type) {
      const variants = {
        bank: 'primary',
        cash: 'success',
        petty_cash: 'info',
      }
      return variants[type] || 'secondary'
    },
    async fetchStatement() {
      const params = {}
      if (this.filters.date_from) params.date_from = this.filters.date_from
      if (this.filters.date_to) params.date_to = this.filters.date_to
      await this.$store.dispatch('accounting/accounts/getStatement', {
        id: this.accountId,
        params,
      })
    },
  },
}
</script>

<style lang="scss">
</style>
